import { createContext, useContext, useState } from "react";

import { useMutation, useQuery } from "@apollo/client/react";

import {
  DraftingCaseUpdateDocument,
  DraftingSectionAddDocument,
  DraftingSectionEditsClearDocument,
  DraftingSectionNotesDocument,
  DraftingSectionRemoveDocument,
  DraftingSectionUpdateDocument,
  type DraftingSourceEnum,
  ProjectDraftingSectionsDocument,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { useDraftingFields } from "./use_drafting_fields";
import { useDraftingSetup } from "./use_drafting_setup";

// Kept the same between renders, so what reads them doesn't re-run.
const NONE: [] = [];

// Its sections, and the chat's citations to check, which leave out a removed or edited section. Apollo
// refetches a skipped query by name too, so with no chat the chat's queries aren't named.
const recheck = (chatId: string | undefined) =>
  chatId
    ? [
        "ConversationDraftSections",
        "ProjectDraftingSections",
        "DashboardConversationsDetails",
      ]
    : ["ProjectDraftingSections"];

// A case's Drafting sections: add, change, his notes, and dropping his edits. On his template's file a
// change to what a section reads goes to his template too; notes and a picked document are this case's
// only. One owner (ReportAssistantSplit) gives it to the views under it.
export const useDraftingSections = (
  projectId: string,
  enabled: boolean,
  chatId: string | undefined,
) => {
  const rechecked = recheck(chatId);
  const { data, refetch } = useQuery(ProjectDraftingSectionsDocument, {
    variables: { id: projectId },
    fetchPolicy: "cache-and-network",
    skip: !enabled,
  });
  const [saveSection, updateState] = useMutation(DraftingSectionUpdateDocument);
  const [saveNotes, notesState] = useMutation(DraftingSectionNotesDocument);
  // The draft is redrawn with the new section's place in it.
  const [insertSection, addState] = useMutation(DraftingSectionAddDocument, {
    refetchQueries: rechecked,
  });
  // The draft is redrawn without the section's place.
  const [dropSection, removeState] = useMutation(
    DraftingSectionRemoveDocument,
    {
      refetchQueries: rechecked,
    },
  );
  // The Report and the download take the chat's text again.
  const [dropEdits, clearState] = useMutation(
    DraftingSectionEditsClearDocument,
    {
      refetchQueries: rechecked,
      awaitRefetchQueries: true,
    },
  );
  // The case's draft and its pages are redrawn with his template's new sections.
  const [takeTemplate, caseUpdateState] = useMutation(
    DraftingCaseUpdateDocument,
    {
      refetchQueries: rechecked,
    },
  );
  // Easy setup's review, while his draft has no sections yet.
  const setup = useDraftingSetup(
    projectId,
    enabled && Boolean(data?.project.draftingSetupOffered),
    rechecked,
  );
  // His fill-in fields, once setup's review, which lists its own, is done.
  const fields = useDraftingFields(
    projectId,
    enabled &&
      Boolean(data?.project.draftingDraft?.displayDocx) &&
      !setup.showing,
    rechecked,
  );
  const [caseOnly, setCaseOnly] = useState(false);
  const [updatedLine, setUpdatedLine] = useState<string | null>(null);
  const project = data?.project;
  // Where a section's add or removal lands, for Mixpanel.
  const target = project?.draftingTemplateVersion != null ? "template" : "case";

  // Only what's given changes. Resolves false when it wasn't saved.
  const updateSection = async (
    id: string,
    input: {
      name?: string;
      instructions?: string;
      sources?: DraftingSourceEnum[];
      supportingDocumentId?: string | null;
    },
  ) => {
    const result = await saveSection({
      variables: {
        id: projectId,
        input: {
          id,
          ...input,
          templateVersion: project?.draftingTemplateVersion,
        },
      },
    }).catch(() => undefined);
    const saved = result?.data?.project.draftingSectionUpdate;
    if (!saved) return false;
    setCaseOnly(!saved.onTemplate);
    track("drafting_section_changed", {
      project_id: projectId,
      target: saved.onTemplate ? "template" : "case",
    });
    return true;
  };

  const setSources = (
    id: string,
    sources: DraftingSourceEnum[],
    supportingDocumentId?: string | null,
  ) => updateSection(id, { sources, supportingDocumentId });

  const setNotes = async (id: string, notes: string) => {
    // Leaving the box saves too, so only a real change is counted.
    const before = project?.draftingSections.find(
      (section) => section.id === id,
    )?.notes;
    const saved = await saveNotes({
      variables: { id: projectId, input: { id, notes } },
    }).catch(() => undefined);
    if (saved && notes !== (before ?? "")) {
      track("drafting_section_notes_saved", { project_id: projectId });
    }
  };

  // A new section where he pointed (`before`, see DraftingSectionAddInput), or
  // after his last one (null). Resolves false when it wasn't saved.
  const addSection = async (input: {
    before: number | null;
    name: string;
    instructions: string;
    sources: DraftingSourceEnum[];
    supportingDocumentId: string | null;
  }) => {
    const result = await insertSection({
      variables: {
        id: projectId,
        input: {
          ...input,
          templateVersion: project?.draftingTemplateVersion,
        },
      },
    }).catch(() => undefined);
    const added = Boolean(result?.data?.project.draftingSectionAdd);
    if (added) {
      track("drafting_section_added", { project_id: projectId, target });
    }
    return added;
  };

  // Resolves false when it wasn't removed.
  const removeSection = async (id: string) => {
    const result = await dropSection({
      variables: {
        id: projectId,
        input: { id, templateVersion: project?.draftingTemplateVersion },
      },
    }).catch(() => undefined);
    const removed = Boolean(result?.data?.project.draftingSectionRemove);
    if (removed) {
      track("drafting_section_removed", { project_id: projectId, target });
    }
    return removed;
  };

  // Drops his edits to a drafted section, so drafting it again replaces them.
  // Resolves false when they weren't dropped.
  const clearEdits = async (id: string) => {
    const result = await dropEdits({
      variables: { id: projectId, input: { id } },
    }).catch(() => undefined);
    const cleared = Boolean(result?.data?.project.draftingSectionEditsClear);
    if (cleared) {
      track("drafting_section_edits_cleared", { project_id: projectId });
    }
    return cleared;
  };

  // Update this case: an older copy of his template takes what changed since.
  const updateCase = async () => {
    const result = await takeTemplate({ variables: { id: projectId } }).catch(
      () => undefined,
    );
    const line = result?.data?.project.draftingCaseUpdate.line;
    if (!line) return;
    setUpdatedLine(line);
    track("drafting_case_updated", { project_id: projectId });
  };

  return {
    projectId,
    refetch,
    // The case's newest draft, the sections he edited, and whether he has a template.
    draft: (enabled && project?.draftingDraft) || null,
    editedSections: (enabled && project?.draftingEditedSections) || NONE,
    hasTemplate: Boolean(enabled && project?.hasDraftTemplate),
    draftable: project?.draftingDraftable ?? false,
    sections: project?.draftingSections ?? NONE,
    changeLine: project?.draftingChangeLine ?? null,
    updatedLine,
    updatingCase: caseUpdateState.loading,
    caseUpdateError: caseUpdateState.error?.message,
    updateCase,
    sectionsLine: project?.draftingSectionsLine ?? null,
    supportingDocuments: project?.supportingDocuments ?? NONE,
    onTemplate: project?.draftingTemplateVersion != null,
    wordDraft: Boolean(project?.draftingDraft?.displayDocx),
    setup,
    fields,
    // Live editing: the draft's id, the file version he edits, and the blocks that stay as they are.
    draftId: project?.draftingDraft?.id,
    fileVersion: project?.draftingDraft?.draftFileVersion,
    locks: project?.draftingDraft?.draftParagraphLocks ?? NONE,
    caseOnly,
    saving: updateState.loading,
    adding: addState.loading,
    error: updateState.error?.message ?? notesState.error?.message,
    addError: addState.error?.message,
    removing: removeState.loading,
    removeError: removeState.error?.message,
    removeSection,
    clearing: clearState.loading,
    clearError: clearState.error?.message,
    clearEdits,
    rechecked,
    setSources,
    updateSection,
    setNotes,
    addSection,
  } as const;
};

export type DraftingSections = ReturnType<typeof useDraftingSections>;

export const DraftingSectionsContext = createContext<DraftingSections | null>(
  null,
);

// The case's sections, from their one owner.
export const useDraftingSectionsOf = () => {
  const drafting = useContext(DraftingSectionsContext);
  if (!drafting) throw new Error("Outside a DraftingSectionsContext");
  return drafting;
};
